import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChatCircleText, PaperPlaneTilt, Info } from "@phosphor-icons/react";

// Extendable list of templates the admin can edit.
const TEMPLATE_FIELDS = [
  { key: "tpl_order_confirmed", label: "Order Confirmed", hint: "Sent when order is created / advance received." },
  { key: "tpl_design_ready", label: "Design Ready", hint: "Sent when design preview is uploaded for approval." },
  { key: "tpl_ready_for_delivery", label: "Ready For Delivery", hint: "Sent when order is fully cut and packed." },
  { key: "tpl_delivered", label: "Delivered", hint: "Sent when order is handed over to customer." },
];

export default function SmsSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testMobile, setTestMobile] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/sms/settings");
      setCfg(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "SMS settings load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const update = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        default_country_code: cfg.default_country_code,
        tpl_order_confirmed: cfg.tpl_order_confirmed,
        tpl_design_ready: cfg.tpl_design_ready,
        tpl_ready_for_delivery: cfg.tpl_ready_for_delivery,
        tpl_delivered: cfg.tpl_delivered,
      };
      const { data } = await api.patch("/sms/settings", payload);
      setCfg(data);
      toast.success("SMS settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testMobile.trim()) {
      toast.error("Enter a mobile number to send the test SMS");
      return;
    }
    setBusy(true);
    try {
      await api.post("/sms/test", {
        mobile: testMobile.trim(),
        message: "Test SMS from Shobhit Creation. Twilio setup working ✓",
      });
      toast.success(`Test SMS sent to ${testMobile}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test SMS failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-slate-400 text-sm" data-testid="sms-settings-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="sms-settings-panel">
      {/* Status card */}
      <div className="border border-slate-700 bg-slate-800/40 rounded-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-orange-300 text-xs font-mono-num tracking-widest">
              <ChatCircleText size={16} weight="fill" /> // TWILIO SMS
            </div>
            <h3 className="text-white font-heading font-bold text-xl mt-1">SMS Notifications</h3>
            <p className="text-slate-400 text-sm mt-1 max-w-xl">
              Fallback for customers who don't use WhatsApp. Sends order updates to any mobile via Twilio.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-mono-num tracking-widest ${cfg.configured ? "text-emerald-400" : "text-red-400"}`} data-testid="sms-configured-badge">
              {cfg.configured ? "● CREDENTIALS OK" : "● CREDENTIALS MISSING"}
            </span>
          </div>
        </div>

        {!cfg.configured && (
          <div className="mt-4 border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 text-amber-200 text-xs flex items-start gap-2" data-testid="sms-creds-warning">
            <Info size={16} weight="bold" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-bold">Twilio credentials not set on server.</div>
              <div className="mt-1 text-amber-100/90">
                Ask developer to set <code className="text-amber-300">TWILIO_ACCOUNT_SID</code>,{" "}
                <code className="text-amber-300">TWILIO_AUTH_TOKEN</code> and{" "}
                <code className="text-amber-300">TWILIO_PHONE_NUMBER</code> in <code>backend/.env</code>, then restart the backend.
              </div>
            </div>
          </div>
        )}

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between border border-slate-700 rounded-sm p-3 bg-slate-900/40">
            <div>
              <Label className="text-slate-300 text-sm">Enable SMS sending</Label>
              <div className="text-xs text-slate-500 mt-1">Turn off to disable the "Send SMS" buttons across the app.</div>
            </div>
            <Switch
              checked={!!cfg.enabled}
              onCheckedChange={(v) => update({ enabled: v })}
              data-testid="sms-enabled-switch"
            />
          </div>
          <div className="border border-slate-700 rounded-sm p-3 bg-slate-900/40">
            <Label className="text-slate-300 text-sm">Default country code</Label>
            <Input
              value={cfg.default_country_code || "+91"}
              onChange={(e) => update({ default_country_code: e.target.value })}
              placeholder="+91"
              className="mt-2 h-9 bg-slate-950 border-slate-700 text-white"
              data-testid="sms-cc-input"
            />
            <div className="text-xs text-slate-500 mt-1">Prefixed to 10-digit mobile numbers automatically.</div>
          </div>
        </div>

        <div className="mt-4 border border-slate-700 rounded-sm p-3 bg-slate-900/40">
          <div className="text-slate-300 text-sm mb-2">Sender number</div>
          <div className="text-white font-mono-num" data-testid="sms-from-number">
            {cfg.from_number || <span className="text-red-400">— not set —</span>}
          </div>
        </div>
      </div>

      {/* Templates */}
      <div className="border border-slate-700 bg-slate-800/40 rounded-sm p-5">
        <div className="text-orange-300 text-xs font-mono-num tracking-widest">// MESSAGE TEMPLATES</div>
        <h3 className="text-white font-heading font-bold text-lg mt-1">Templates</h3>
        <p className="text-slate-400 text-xs mt-1">
          Use placeholders <code className="text-orange-300">{"{name}"}</code>,{" "}
          <code className="text-orange-300">{"{order_no}"}</code>,{" "}
          <code className="text-orange-300">{"{link}"}</code>.
        </p>
        <div className="space-y-4 mt-4">
          {TEMPLATE_FIELDS.map((tpl) => (
            <div key={tpl.key}>
              <Label className="text-slate-300 text-sm">{tpl.label}</Label>
              <textarea
                value={cfg[tpl.key] || ""}
                onChange={(e) => update({ [tpl.key]: e.target.value })}
                rows={2}
                className="w-full mt-1 bg-slate-950 border border-slate-700 rounded-sm p-2 text-white text-sm font-mono-num"
                data-testid={`sms-template-${tpl.key}`}
              />
              <div className="text-xs text-slate-500 mt-1">{tpl.hint}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions + test */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Input
            value={testMobile}
            onChange={(e) => setTestMobile(e.target.value)}
            placeholder="Test mobile e.g. 9012345678"
            className="h-10 bg-slate-950 border-slate-700 text-white w-56"
            data-testid="sms-test-mobile"
          />
          <Button
            onClick={sendTest}
            disabled={busy || !cfg.configured}
            variant="outline"
            className="rounded-sm h-10 border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="sms-test-send-btn"
          >
            <PaperPlaneTilt size={16} className="mr-1.5" /> SEND TEST SMS
          </Button>
        </div>
        <Button
          onClick={save}
          disabled={busy}
          className="rounded-sm h-10 bg-orange-500 hover:bg-orange-600 text-white font-mono-num tracking-widest text-xs"
          data-testid="sms-save-btn"
        >
          {busy ? "SAVING…" : "SAVE SETTINGS"}
        </Button>
      </div>
    </div>
  );
}
